import apiController from '@/utils/apiController';
import EventBus from '@/utils/eventBus';

const RESOLVE_PAGE_SIZE = 200;
const RESOLVE_MAX_PAGES = 10000;
/** Itens por página no dropdown (primeira busca e cada "carregar mais"). */
const LAZY_PAGE_SIZE = 10;

/**
 * Shared lazy-fetch behaviour for Merus select components.
 * Consumers own the items list; call fetchLazyItems and mergeLazyItems.
 */
export default {
    props: {
        getUrl: {
            type: String,
            default: null,
        },
        showInactives: {
            type: Boolean,
            default: false,
        },
        /** Extra fields merged into the lazy POST body (e.g. id_unidades). */
        requestData: {
            type: Object,
            default: null,
        },
        /** Alias of requestData (some screens still bind extra-data). */
        extraData: {
            type: Object,
            default: null,
        },
        /** Optional post-fetch filter (e.g. exclude mutually selected fence). */
        lazyItemFilter: {
            type: Function,
            default: null,
        },
    },
    data: () => ({
        lazyLoading: false,
        lazySearch: '',
        lazyRequestId: 0,
        /** Total matching current filters (when API returns `total`). */
        lazyTotal: null,
        /** Última página carregada da busca atual (rolagem infinita do dropdown). */
        lazyPage: 1,
        /** Itens brutos (antes de lazyItemFilter) já carregados na busca atual. */
        lazyLoadedCount: 0,
        lazyHasMore: false,
        lazyLoadingMore: false,
        /** @type {Record<string|number, object>} */
        selectedItemCache: {},
    }),
    computed: {
        lazyRequestPayload() {
            if (this.requestData && typeof this.requestData === 'object') {
                return this.requestData;
            }
            if (this.extraData && typeof this.extraData === 'object') {
                return this.extraData;
            }
            return {};
        },
    },
    watch: {
        getUrl(newUrl, oldUrl) {
            if (!this.lazy || newUrl === oldUrl) return;
            this.lazyItems = [];
            this.lazyTotal = null;
            this.lazyHasMore = false;
            this.lazySearch = '';
            this.selectedItemCache = {};
            if (typeof this.onLazyFiltersChanged === 'function') {
                this.onLazyFiltersChanged();
            }
            if (typeof this.loadLazyItems === 'function') {
                this.loadLazyItems('');
            }
        },
        lazyRequestPayload: {
            deep: true,
            handler(val, oldVal) {
                if (JSON.stringify(val || {}) === JSON.stringify(oldVal || {})) {
                    return;
                }
                // MultiplaEscolha may use the mixin without lazy mode
                if (this.lazy === false) {
                    return;
                }
                if (typeof this.onLazyFiltersChanged === 'function') {
                    this.onLazyFiltersChanged();
                }
                if (typeof this.loadLazyItems === 'function') {
                    this.loadLazyItems(this.lazySearch);
                }
            },
        },
    },
    methods: {
        /**
         * Cache item objects by their value key so labels survive search changes.
         * @param {object[]} items
         * @param {string} valueKey
         */
        cacheSelectedItems(items, valueKey) {
            if (!Array.isArray(items) || !valueKey) return;
            items.forEach((item) => {
                if (item == null || item[valueKey] == null || item[valueKey] === '') return;
                const key = String(item[valueKey]);
                this.$set
                    ? this.$set(this.selectedItemCache, key, item)
                    : (this.selectedItemCache[key] = item);
            });
        },

        /**
         * Concatena páginas sem repetir itens (inserções no banco entre uma página
         * e outra deslocam o OFFSET e podem trazer o mesmo registro duas vezes).
         */
        appendUniqueLazyItems(current, incoming, valueKey) {
            const list = Array.isArray(current) ? [...current] : [];
            const seen = new Set(
                list
                    .filter((item) => item != null && item[valueKey] != null)
                    .map((item) => String(item[valueKey]))
            );
            (Array.isArray(incoming) ? incoming : []).forEach((item) => {
                if (item == null) return;
                const id = item[valueKey];
                if (id != null && id !== '') {
                    const key = String(id);
                    if (seen.has(key)) return;
                    seen.add(key);
                }
                list.push(item);
            });
            return list;
        },

        getCachedSelectedItem(val) {
            if (val == null || val === '') return null;
            return this.selectedItemCache[String(val)] || null;
        },

        applyLazyItemFilter(items) {
            const list = Array.isArray(items) ? items : [];
            if (typeof this.lazyItemFilter === 'function') {
                return this.lazyItemFilter(list) || [];
            }
            return list;
        },

        /**
         * Ensure selected values stay represented in the items list.
         * @param {object[]} fetchedItems
         * @param {Array|*} selectedValues single value or array of values
         * @param {string} valueKey
         * @returns {object[]}
         */
        mergeLazyItems(fetchedItems, selectedValues, valueKey) {
            const fetched = Array.isArray(fetchedItems) ? [...fetchedItems] : [];
            this.cacheSelectedItems(fetched, valueKey);

            const values = Array.isArray(selectedValues)
                ? selectedValues
                : selectedValues != null && selectedValues !== ''
                    ? [selectedValues]
                    : [];

            const seen = new Set(
                fetched
                    .filter((item) => item != null && item[valueKey] != null && item[valueKey] !== '')
                    .map((item) => String(item[valueKey]))
            );
            values.forEach((val) => {
                if (val == null || val === '') return;
                const key = String(val);
                if (seen.has(key)) return;
                const cached = this.getCachedSelectedItem(val);
                if (cached) {
                    fetched.push(cached);
                    seen.add(key);
                }
            });
            return fetched;
        },

        buildLazyRequestBody({
            search = this.lazySearch,
            page = 1,
            pageSize = 10,
            includeTotal = true,
            requestDataOverride = null,
        } = {}) {
            const extra = (requestDataOverride && typeof requestDataOverride === 'object')
                ? requestDataOverride
                : (this.lazyRequestPayload || {});
            return {
                page,
                pageSize,
                search: search == null ? '' : String(search),
                showInactives: this.showInactives,
                includeTotal: !!includeTotal,
                ...extra,
            };
        },

        /**
         * Fetch one page from the lazy endpoint (Promise).
         * @returns {Promise<{ items: object[], total: number|null }>}
         */
        fetchLazyPage({
            search = this.lazySearch,
            page = 1,
            pageSize = 10,
            includeTotal = true,
            silent = false,
            requestDataOverride = null,
        } = {}) {
            const endpoint = apiController.endpoints[this.getUrl];
            if (!endpoint) {
                const err = new Error(`Endpoint não encontrado: ${this.getUrl}`);
                EventBus.$emit('show-error', { error: err.message });
                return Promise.reject(err);
            }

            return apiController
                .request(endpoint, {
                    data: this.buildLazyRequestBody({
                        search,
                        page,
                        pageSize,
                        includeTotal,
                        requestDataOverride,
                    }),
                    silent,
                })
                .then((response) => {
                    if (response.data?.erro) {
                        const msg = response.data.mensagem || 'Erro ao carregar itens';
                        EventBus.$emit('show-error', { error: msg });
                        return Promise.reject(new Error(msg));
                    }
                    const items = response.data.items || [];
                    const total =
                        response.data.total != null && response.data.total !== ''
                            ? Number(response.data.total)
                            : null;
                    return {
                        items,
                        total: Number.isFinite(total) ? total : null,
                    };
                });
        },

        /**
         * Paginate through the lazy endpoint until exhausted.
         * Stops on an empty page (not on partial pages — post-filter may shrink pages).
         * @returns {Promise<{ items: object[], total: number|null }>}
         */
        async fetchAllLazyItems({
            search = this.lazySearch,
            pageSize = RESOLVE_PAGE_SIZE,
            onProgress,
            requestDataOverride = null,
        } = {}) {
            const all = [];
            let page = 1;
            let total = null;
            const chunk = Number(pageSize) > 0 ? Math.floor(Number(pageSize)) : RESOLVE_PAGE_SIZE;

            while (page < RESOLVE_MAX_PAGES) {
                const result = await this.fetchLazyPage({
                    search,
                    page,
                    pageSize: chunk,
                    includeTotal: page === 1,
                    silent: true,
                    requestDataOverride,
                });
                if (result.total != null) {
                    total = result.total;
                }
                const items = Array.isArray(result.items) ? result.items : [];
                if (items.length === 0) {
                    break;
                }
                all.push(...items);
                if (typeof onProgress === 'function') {
                    onProgress({
                        loaded: all.length,
                        total,
                        page,
                    });
                }
                if (total != null && all.length >= total) {
                    break;
                }
                page += 1;
            }

            return { items: all, total: total != null ? total : all.length };
        },

        updateLazyPaging(page, fetchedCount) {
            this.lazyPage = page;
            this.lazyLoadedCount = page === 1 ? fetchedCount : this.lazyLoadedCount + fetchedCount;
            // Página cheia sem total conhecido: assume que pode haver mais.
            this.lazyHasMore = this.lazyTotal != null
                ? this.lazyLoadedCount < this.lazyTotal
                : fetchedCount >= LAZY_PAGE_SIZE;
        },

        /**
         * Fetch the first page of items from the lazy endpoint (UI dropdown).
         * @param {string|null|undefined} searchVal
         * @param {{ onSuccess: (items: object[], meta?: { total: number|null }) => void }} options
         */
        fetchLazyItems(searchVal, { onSuccess } = {}) {
            this.lazySearch = searchVal == null ? '' : String(searchVal);
            const endpoint = apiController.endpoints[this.getUrl];
            if (!endpoint) {
                EventBus.$emit('show-error', { error: `Endpoint não encontrado: ${this.getUrl}` });
                return;
            }

            const currentRequest = ++this.lazyRequestId;
            this.lazyLoading = true;
            this.lazyLoadingMore = false;
            this.fetchLazyPage({
                search: this.lazySearch,
                page: 1,
                pageSize: LAZY_PAGE_SIZE,
                includeTotal: true,
            })
                .then(({ items, total }) => {
                    if (currentRequest !== this.lazyRequestId) {
                        return;
                    }
                    this.lazyTotal = total;
                    this.updateLazyPaging(1, items.length);
                    if (typeof onSuccess === 'function') {
                        onSuccess(items, { total });
                    }
                })
                .catch(() => {
                    // errors already surfaced via EventBus / apiController
                })
                .finally(() => {
                    if (currentRequest !== this.lazyRequestId) {
                        return;
                    }
                    this.lazyLoading = false;
                });
        },

        /**
         * Próxima página da busca atual (rolagem até o fim do dropdown). Uma nova
         * busca invalida a requisição em andamento via lazyRequestId.
         * @param {{ onSuccess: (items: object[]) => void }} options
         */
        fetchMoreLazyItems({ onSuccess } = {}) {
            if (!this.lazyHasMore || this.lazyLoading || this.lazyLoadingMore) return;
            const requestId = this.lazyRequestId;
            const nextPage = this.lazyPage + 1;
            this.lazyLoadingMore = true;
            this.fetchLazyPage({
                search: this.lazySearch,
                page: nextPage,
                pageSize: LAZY_PAGE_SIZE,
                includeTotal: false,
                silent: true,
            })
                .then(({ items }) => {
                    if (requestId !== this.lazyRequestId) return;
                    this.updateLazyPaging(nextPage, items.length);
                    if (typeof onSuccess === 'function') {
                        onSuccess(items);
                    }
                })
                .catch(() => {
                    if (requestId !== this.lazyRequestId) return;
                    this.lazyHasMore = false;
                })
                .finally(() => {
                    if (requestId !== this.lazyRequestId) return;
                    this.lazyLoadingMore = false;
                });
        },

        /** v-intersect do rodapé do dropdown: carrega a próxima página ao ficar visível. */
        onLazyEndIntersect(entries, observer, isIntersecting) {
            if (isIntersecting && typeof this.loadMoreLazyItems === 'function') {
                this.loadMoreLazyItems();
            }
        },
    },
};
